import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import {
  CAMPAIGN_ENTITY_VARIANTS,
  campaignEntityBadgeSx,
  campaignEntityEditButtonSx,
  campaignEntityHeaderSx,
  campaignEntityPanelSx,
  campaignEntityTimestampChipSx,
} from "./campaignEntityStyles"

/**
 * Top-level wrapper for a distinct Campaign tab entity (profile vs lead targeting).
 *
 * @param {object} props
 * @param {"recruitingProfile" | "leadTargeting"} props.variant
 * @param {React.ReactNode} props.icon
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {string} [props.timestampLabel]
 * @param {string} [props.editTo] Router path to open the editor.
 * @param {string} [props.editLabel]
 * @param {React.ReactNode} props.children
 */
export default function CampaignEntityPanel({
  variant,
  icon,
  title,
  description,
  timestampLabel,
  editTo,
  editLabel = "Edit",
  children,
}) {
  const theme = useTheme()
  const meta = CAMPAIGN_ENTITY_VARIANTS[variant]

  return (
    <Box
      component="section"
      aria-label={title}
      sx={campaignEntityPanelSx(theme, variant)}
    >
      <Box sx={campaignEntityHeaderSx(theme, variant)}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              minWidth: 0,
              flex: "1 1 280px",
            }}
          >
            <Box
              className="campaign-entity-icon"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: 1.5,
                flexShrink: 0,
                boxShadow: "0 2px 6px -2px rgba(0,0,0,0.08)",
                "& .MuiSvgIcon-root": { fontSize: 26 },
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Chip
                  label={meta.badge}
                  size="small"
                  sx={campaignEntityBadgeSx(theme, variant)}
                />
                {timestampLabel ? (
                  <Chip
                    label={timestampLabel}
                    size="small"
                    variant="outlined"
                    sx={campaignEntityTimestampChipSx(theme, variant)}
                  />
                ) : null}
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              {description ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, lineHeight: 1.5, maxWidth: 600 }}
                >
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {editTo ? (
            <Button
              component={RouterLink}
              to={editTo}
              variant="outlined"
              size="small"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={campaignEntityEditButtonSx(theme, variant)}
            >
              {editLabel}
            </Button>
          ) : null}
        </Box>
      </Box>
      <Column
        sx={{
          gap: 2,
          alignItems: "stretch",
          px: { xs: 1.5, sm: 2 },
          py: 2,
          width: "100%",
        }}
      >
        {children}
      </Column>
    </Box>
  )
}
